import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Play, Video, ShoppingBag, ChevronLeft } from 'lucide-react';
import { fetchMyPurchases, type ProductPurchase } from '@/services/creatorApi';

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const MyPurchasesPage: React.FC = () => {
  const [purchases, setPurchases] = useState<ProductPurchase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyPurchases()
      .then(setPurchases)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: 'var(--hj-bg)' }}
      >
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
        <p className="m-0" style={{ marginTop: 12, fontSize: 13, color: 'var(--hj-ink-3)' }}>
          Loading purchases…
        </p>
      </main>
    );
  }

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 800,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>My Purchases</span>
      </div>

      <header
        style={{
          margin: '4px 0 24px',
          padding: '22px 24px 20px',
          borderRadius: 'var(--hj-r-md)',
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-beige-ink-2)' }}>My Purchases</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(28px, 4vw, 40px)',
            lineHeight: 1,
            color: 'var(--hj-beige-ink)',
            letterSpacing: '-0.025em',
            fontWeight: 700,
          }}
        >
          Your stuff.
        </h1>
      </header>

      {purchases.length === 0 ? (
        <div
          className="text-center"
          style={{
            padding: '60px 24px',
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1.5px dashed var(--hj-hairline)',
          }}
        >
          <ShoppingBag className="w-10 h-10 mx-auto" style={{ color: 'var(--hj-ink-3)', marginBottom: 12 }} />
          <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink-3)' }}>
            Nothing purchased yet.
          </p>
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5"
            style={{
              marginTop: 16,
              padding: '8px 16px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
              fontFamily: 'inherit',
              minHeight: 40,
            }}
          >
            Browse the marketplace
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {purchases.map(purchase => {
            const product = purchase.product;
            return (
              <div
                key={purchase._id}
                className="flex items-center gap-4"
                style={{
                  padding: 14,
                  borderRadius: 'var(--hj-r-md)',
                  background: 'var(--hj-surface-1)',
                  border: '1px solid var(--hj-hairline)',
                }}
              >
                <div
                  className="rounded-md flex items-center justify-center flex-shrink-0 relative overflow-hidden"
                  style={{ width: 88, height: 60, background: 'var(--hj-surface-2)' }}
                >
                  {product?.videos?.[0]?.thumbnailUrl && (
                    <img
                      src={
                        product.videos[0].thumbnailUrl.startsWith('http')
                          ? product.videos[0].thumbnailUrl
                          : `/api/s3/${product.videos[0].thumbnailUrl}`
                      }
                      className="w-full h-full object-cover rounded-md"
                      alt={product.title}
                      onError={e => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <Video className="w-5 h-5 absolute" style={{ color: 'var(--hj-ink-4)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="truncate m-0"
                    style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}
                  >
                    {product?.title || 'Untitled'}
                  </p>
                  <div
                    className="flex items-center gap-2"
                    style={{ marginTop: 4, fontSize: 12, color: 'var(--hj-ink-3)' }}
                  >
                    <span>
                      {product?.productType === 'webinar'
                        ? 'Featured event'
                        : product?.productType === 'series'
                        ? `${product.videos.length} video${product.videos.length === 1 ? '' : 's'}`
                        : 'Single video'}
                    </span>
                    <span style={{ color: 'var(--hj-ink-4)' }}>·</span>
                    <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>
                      {/* Featured events: show the session they registered for, not the purchase date. */}
                      {product?.productType === 'webinar'
                        ? new Date(purchase.eventDate || product.eventDate || purchase.purchasedAt).toLocaleDateString()
                        : new Date(purchase.purchasedAt).toLocaleDateString()}
                    </span>
                    {typeof purchase.price === 'number' && (
                      <>
                        <span style={{ color: 'var(--hj-ink-4)' }}>·</span>
                        <span
                          className="tabular"
                          style={{ fontFamily: 'var(--hj-font-mono)', color: 'var(--hj-ink-2)' }}
                        >
                          {formatPrice(purchase.price)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <Link
                  to={`/purchases/${purchase.productId}`}
                  className="inline-flex items-center gap-1.5 flex-shrink-0"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: 'var(--hj-blue)',
                    border: '1px solid var(--hj-blue)',
                    color: 'oklch(0.18 0.005 70)',
                    fontSize: 12.5,
                    fontWeight: 700,
                    textDecoration: 'none',
                    fontFamily: 'inherit',
                    minHeight: 36,
                  }}
                >
                  <Play className="w-3.5 h-3.5" /> Watch
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
};

export default MyPurchasesPage;
